import React, { useState, useEffect, useCallback } from 'react';
import { useAvivaSelector } from '@redux/hook';
import { useHistory } from 'react-router-dom';
import { GAAviva } from '@utils/gaAviva';

import { SQSPocketResponse } from '@models/SQSPocketRequest.model';
import { MyInfoForm } from '@models/DPI/MyInfo.model';
import { AboutYouForm, AboutYouHealthForm } from '@models/DPI/AboutYou.model';
import { DeclarationForm } from '@models/DPI/Declaration.model';


import IconPencil from '@assets/icon/pencil.svg';
import Button from '@components/Button';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import InformationBox from '@components/InformationBox';
import Accordion from '@components/Accordion';
import Link from '@components/Link';
import Listing from '@components/Listing';
import Bullet from '@components/Bullet';
import BulletList from '@components/BulletList';
import Text from '@components/Text';
import ContactBox from '@components/ContactBox';
import Modal from '@components/Modal';
import Divider from '@components/Divider';
import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';

const importantNotes = [
  'A life policy is not a savings account or deposit.',
  'As this product has no cash savings or investment feature, there is no cash value if the policy ends or if the policy is terminated prematurely.',
  "There is a 14-day free-look period after you've received the policy contract.",
  'You may ask us to explain the product features to you.',
  'You may wish to separately seek advice on the suitability of the life policy.',
  'In the event that you choose not to seek advice on the suitability of the life policy, you should consider if the life policy is suitable for your financial circumstances and needs.'
];


const trackOnload = (): void => {
  const GA = GAAviva.getInstance();
  let { form } = GA.digitalData;
  form.name = "Review";
  GA.trackPageLoad({ form });
}

interface IItem { text: string, value: string };
export interface ReviewProps { }

function Review(props: ReviewProps) {
  const history = useHistory();

  const contactInformation = useAvivaSelector(state => state.static.contactInformation);
  const quoteRawResponse = useAvivaSelector(state => state.dpiStep1.quoteRawResponse);
  const myInfoForm = useAvivaSelector(state => state.dpiStep2.myInfoForm);
  const myInfoAddressForm = useAvivaSelector(state => state.dpiStep2.myInfoAddressForm);
  const income =  useAvivaSelector(state => state.dpiStep3.income);
  const health =  useAvivaSelector(state => state.dpiStep3.health);
  const declaration = useAvivaSelector(state => state.dpiStep4.declaration);
  const isStep4Completed = useAvivaSelector(state => state.dpiStep4.isCompleted);


  const [planDetails, setPlanDetails] = useState<IItem[]>([]);
  const [personalDetails, setPersonalDetails] = useState<IItem[]>([]);
  const [otherDetails, setOtherDetails] = useState<IItem[]>([]);
  const [isShowConfirm, setIsShowConfirm] = useState<boolean>(false);
  const [incomeQuestions, setIncomeQuestions] = useState<IItem[]>([]);
  const [healthQuestions, setHealthQuestions] = useState<IItem[]>([]);
  const [declarations, setDeclarations] = useState<IItem[]>([]);

  const onClickContinue = () => {
    history.push('/dpi/payment')
  };
  const onBackMyInfo = (step: number) => {
    history.push(`/dpi/my-info?step=${step}`);
  }

  const onBack = useCallback(() => {
    history.push('/dpi/declaration')
  }, [history])

  const onBackCoverage = () => {
    history.push('/dpi/coverage')

  }

  const onBackAboutYou = (step: number) => {
    history.push(`/dpi/about-you?step=${step}`)

  }

  useEffect(() => {
    if (!isStep4Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
      onBack();
    }
    if(quoteRawResponse) {
      const planQuotation = new SQSPocketResponse(quoteRawResponse);
      setPlanDetails(planQuotation.dpiPlanDetails);
      trackOnload();
    }

    if (myInfoForm) {
      const info = new MyInfoForm(myInfoForm);
      info.extraAddress = myInfoAddressForm;
      console.log(info, myInfoForm);
      setPersonalDetails(info.reviewPersonalDetails);
      setOtherDetails(info.reviewOtherDetails);
    }

    if (declaration) {
      const declarationForm = new DeclarationForm(declaration);
      setDeclarations(declarationForm.reviewDeclaration);
    }

    if (income) {
      const aboutYouIncome = new AboutYouForm(income);
      setIncomeQuestions(aboutYouIncome.reviewIncome);
    }

    if (health) {
      const aboutYouHealth = new AboutYouHealthForm(health);
      setHealthQuestions(aboutYouHealth.reviewHealth);
      console.log(aboutYouHealth.reviewHealth);
    }
  }, [quoteRawResponse, myInfoForm, health, declaration, income, myInfoAddressForm, isStep4Completed, onBack]);

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={4} label="Review" />
          <Heading margin="0 0 30px"> Review </Heading>
        </FullGrid>
        <LeftGrid>

          <InformationBox variant="error">
            <Text><b>WARNING: </b>Pursuant to Section 23(5) of the Insurance Act 1966, you are to disclose in this application form fully and faithfully all facts which you know or ought to know. Otherwise the insurance effected may be void.</Text>
          </InformationBox>
          <Accordion
            isExpanded
            title="Your plan details"
            rightElement={<Link text="Edit" onClick={() => { onBackCoverage() }} variant="button" icon={IconPencil} />}>
            <Listing  margin={0} sources={planDetails} variant="alternate-color" />
          </Accordion>

          <Divider size={2} margin="0 0 30px" />
          <InformationBox hasClose>
            <Text>Personal details pulled from Myinfo can’t be edited. To amend, please proceed to <Link variant="button" onClick={() => { setIsShowConfirm(true) }} text="Myinfo"></Link></Text>
          </InformationBox>

          <Accordion title="Personal details retrieved from Myinfo" isExpanded>
            <Listing sources={personalDetails} variant="alternate-color" />
          </Accordion>
          <Divider size={2} margin="0 0 20px" />

          <Accordion
            isExpanded
            title="Other details"
            rightElement={<Link text="Edit" onClick={() => { onBackMyInfo(1) }} variant="button" icon={IconPencil} />}>
            <Listing  margin={0} sources={otherDetails} variant="alternate-color" />
          </Accordion>
          <Divider size={2} margin="0 0 20px" />

          <Accordion
            title="Income Questions"
            rightElement={<Link text="Edit" onClick={() => { onBackAboutYou(0) }} variant="button" icon={IconPencil}/>}
          >
            <Listing margin={0} sources={incomeQuestions} variant="alternate-color" />
          </Accordion>
          <Divider size={1} margin="0 0 20px" />
        
          <Accordion
            title="Health Questions"
            rightElement={<Link text="Edit" onClick={() => { onBackAboutYou(1) }} variant="button" icon={IconPencil} />}>
            <Listing  margin={0}  sources={healthQuestions} variant="alternate-color" />
          </Accordion>
          <Divider size={1} margin="0 0 20px" />

          <Accordion
            title="Declaration"
            rightElement={<Link text="Edit" onClick={() => { onBack() }} variant="button" icon={IconPencil} />}>
            <Listing  margin={0} sources={declarations} variant="alternate-color" />
          </Accordion>
          <Divider size={1} margin="0 0 20px" />

          <Accordion title="Important notes" isExpanded>
            <BulletList>
              {importantNotes.map((note: string, index: number) => (<Bullet key={`${index}`}>{note}</Bullet>))}
            </BulletList>
          </Accordion>

          <Button onClick={onClickContinue} label="Continue" margin="35px 0 20px 0" />
          <Button variant="secondary" onClick={onBack} label="Back" margin="0 0 40px" />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid>

        <Modal show={isShowConfirm} onClose={() => setIsShowConfirm(false)}>
          <Heading margin="0 0 20px" marginTablet="0 0 30px" variant="sub">You will be redirected to Myinfo</Heading>
          <Text margin="0 0 20px">Please note that by proceeding to edit your personal information in Myinfo, you are required to sign  in with your Singpass again. All information captured in this application will be removed.
          </Text>
          <Text margin="0 0 30px">Do allow time interval for your Myinfo profile to be updated before starting this application again.</Text>
          <Button label="Proceed" onClick={() => { onBackMyInfo(0)}}></Button>
          <Button margin={0} label="Cancel" variant="secondary"></Button>
        </Modal>
      </ContainerGrid>
    </Container>

  );
};

export default Review;
